/**
 * CheckoutBridgePage
 * Handles mixed cart checkouts (subscription + one-time)
 * Redirects to second session after first completes
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  AppBar,
  Toolbar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import { trackPageView } from '../lib/analytics';

const CheckoutBridgePage: React.FC = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const firstSessionId = searchParams.get('first_session_id');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackPageView('checkout_bridge');
    
    if (firstSessionId) {
      handleBridgeRedirect();
    } else {
      setError('Missing session information. Please try checkout again.');
      setLoading(false);
    }
  }, [firstSessionId]);

  const handleBridgeRedirect = async () => {
    try {
      // Get the second session URL from backend
      const response = await api.get(`/checkout/bridge/${firstSessionId}`);
      
      if (response.data.success && response.data.data.secondSessionUrl) {
        // Redirect to the second checkout session
        window.location.href = response.data.data.secondSessionUrl;
      } else if (response.data.data.complete) {
        // Both sessions complete, redirect to success
        window.location.href = `/checkout/success?session_id=${firstSessionId}`;
      } else {
        throw new Error('Could not determine next checkout step');
      }
    } catch (err: any) {
      console.error('Bridge error:', err);
      setError(
        err.response?.data?.error?.message ||
        'Failed to continue checkout. Please contact support.'
      );
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 'bold',
            }}
          >
            REDEX
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 8 }}>
        {loading ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <SyncIcon 
                sx={{ 
                  fontSize: 50, 
                  color: 'primary.main',
                  animation: 'spin 2s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }} 
              />
            </Box>
            
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Processing Your Order
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your subscription has been set up. Redirecting to complete the one-time payment...
            </Typography>
            <CircularProgress size={30} />
          </Paper>
        ) : error ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              If this problem persists, please contact our support team with your order details.
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="contained"
                component={Link}
                to="/cart"
              >
                Return to Cart
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to="/contact"
              >
                Contact Support
              </Button>
            </Box>
          </Paper>
        ) : null}
      </Container>
    </Box>
  );
};

export default CheckoutBridgePage;
